import { useQueryClient } from '@tanstack/react-query';
import { recallDataKeys } from '@/features/admin/hooks/useCachedRecallData';
import { zoneKeys } from '@/features/admin/hooks/useZones';

/**
 * Utility to clear all caches related to zone map data
 * Use this if recalls aren't showing due to stale cache
 */

let queryClientInstance: any = null;

// Store query client instance when available
export function setQueryClientInstance(queryClient: any) {
  queryClientInstance = queryClient;
}

export async function clearZoneMapCaches() {
  if (!queryClientInstance) {
    console.error('❌ Query client not available. Try refreshing the page.');
    return false;
  }

  try {
    console.log('🧹 Clearing zone map caches...');

    // 1. Clear all recall data caches
    console.log('📊 Clearing recall data caches...');
    await queryClientInstance.invalidateQueries({
      queryKey: recallDataKeys.all
    });

    // Remove all recall data from cache immediately
    queryClientInstance.removeQueries({
      queryKey: recallDataKeys.all
    });

    // 2. Clear zone caches
    console.log('🗺️ Clearing zone caches...');
    await queryClientInstance.invalidateQueries({
      queryKey: zoneKeys.all
    });

    queryClientInstance.removeQueries({
      queryKey: zoneKeys.all
    });

    // 3. Clear spatial clustering caches (if any)
    console.log('📍 Clearing spatial clustering caches...');
    await queryClientInstance.invalidateQueries({
      queryKey: ['spatialClusters']
    });

    queryClientInstance.removeQueries({
      queryKey: ['spatialClusters']
    });

    // 4. Force garbage collection on query cache
    console.log('🗑️ Running garbage collection...');
    queryClientInstance.clear();

    console.log('✅ All zone map caches cleared successfully!');
    console.log('🔄 Next map interaction will fetch fresh data from the database.');

    return true;
  } catch (error) {
    console.error('❌ Failed to clear caches:', error);
    return false;
  }
}

/**
 * Clear caches and provide detailed feedback about what was cleared
 */
export async function clearZoneMapCachesDetailed() {
  if (!queryClientInstance) {
    console.error('❌ Query client not available. Try refreshing the page.');
    return false;
  }

  try {
    console.log('🧹 Clearing zone map caches with detailed feedback...');

    // Get current cache state
    const cache = queryClientInstance.getQueryCache();
    const allQueries = cache.getAll();

    const recallQueries = allQueries.filter(query =>
      query.queryKey.some(key => typeof key === 'string' && key.includes('recallData'))
    );

    const zoneQueries = allQueries.filter(query =>
      query.queryKey.some(key => typeof key === 'string' && key.includes('zones'))
    );

    console.log(`📊 Found ${recallQueries.length} recall data cache entries`);
    console.log(`🗺️ Found ${zoneQueries.length} zone cache entries`);

    // Show sample cache keys
    if (recallQueries.length > 0) {
      console.log('📊 Sample recall cache keys:');
      recallQueries.slice(0, 3).forEach((query, i) => {
        console.log(`  ${i + 1}. ${JSON.stringify(query.queryKey)}`);
      });
    }

    if (zoneQueries.length > 0) {
      console.log('🗺️ Sample zone cache keys:');
      zoneQueries.slice(0, 3).forEach((query, i) => {
        console.log(`  ${i + 1}. ${JSON.stringify(query.queryKey)}`);
      });
    }

    // Clear everything
    const cleared = await clearZoneMapCaches();

    if (cleared) {
      const remainingQueries = queryClientInstance.getQueryCache().getAll();
      console.log(
        `🧹 Cache cleared! Remaining entries: ${remainingQueries.length} (down from ${allQueries.length})`
      );
    }

    return cleared;
  } catch (error) {
    console.error('❌ Failed to clear caches with details:', error);
    return false;
  }
}

/**
 * Check current cache state without clearing
 */
export function inspectZoneMapCaches() {
  if (!queryClientInstance) {
    console.error('❌ Query client not available. Try refreshing the page.');
    return;
  }

  const cache = queryClientInstance.getQueryCache();
  const allQueries = cache.getAll();

  console.log('🔍 Inspecting zone map caches...');
  console.log(`📊 Total cache entries: ${allQueries.length}`);

  const recallQueries = allQueries.filter(query =>
    query.queryKey.some(key => typeof key === 'string' && key.includes('recallData'))
  );

  const zoneQueries = allQueries.filter(query =>
    query.queryKey.some(key => typeof key === 'string' && key.includes('zones'))
  );

  console.log(`📊 Recall data cache entries: ${recallQueries.length}`);
  console.log(`🗺️ Zone cache entries: ${zoneQueries.length}`);

  // Show cache ages
  const now = Date.now();
  recallQueries.forEach((query, i) => {
    if (i < 3) {
      // Show first 3
      const age = query.state.dataUpdatedAt
        ? Math.round((now - query.state.dataUpdatedAt) / 1000 / 60)
        : 'unknown';
      console.log(`  📊 Entry ${i + 1}: ${age} minutes old, state: ${query.state.status}`);
    }
  });

  zoneQueries.forEach((query, i) => {
    if (i < 3) {
      // Show first 3
      const age = query.state.dataUpdatedAt
        ? Math.round((now - query.state.dataUpdatedAt) / 1000 / 60)
        : 'unknown';
      console.log(`  🗺️ Entry ${i + 1}: ${age} minutes old, state: ${query.state.status}`);
    }
  });

  return {
    totalEntries: allQueries.length,
    recallEntries: recallQueries.length,
    zoneEntries: zoneQueries.length
  };
}

// Export functions to window for console access
if (typeof window !== 'undefined') {
  (window as any).clearZoneMapCaches = clearZoneMapCaches;
  (window as any).clearZoneMapCachesDetailed = clearZoneMapCachesDetailed;
  (window as any).inspectZoneMapCaches = inspectZoneMapCaches;
}
